import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { LinksFunction, MetaFunction } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useRouteError,
} from '@remix-run/react';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Toaster, toast } from 'sonner';

import { getErrorMessage } from '~/lib/errors';
import { Sentry, sentryEnabled } from '~/lib/sentry';
import globalStyles from '~/styles/global.css?url';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://studio.deleg8.dev';

export const meta: MetaFunction = () => [
  { title: 'Deleg8 Studio — AI-Powered Creative Marketing Platform' },
  { name: 'description', content: 'Delegate your creative work to AI. Deleg8 Studio captures brand DNA, builds narratives, and generates multi-channel creative assets.' },
  { name: 'theme-color', content: '#7e22ce' },
  { property: 'og:type', content: 'website' },
  { property: 'og:title', content: 'Deleg8 Studio — Delegate Your Creative Work to AI' },
  { property: 'og:description', content: 'AI-powered creative marketing platform. Capture brand context, build narratives, and generate multi-channel campaigns.' },
  { property: 'og:image', content: `${SITE_URL}/og-image.png` },
  { property: 'og:image:width', content: '1366' },
  { property: 'og:image:height', content: '768' },
  { property: 'og:site_name', content: 'Deleg8 Studio' },
  { property: 'og:locale', content: 'en_US' },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Deleg8 Studio — AI-Powered Creative Marketing' },
  { name: 'twitter:description', content: 'Delegate your creative work to AI.' },
  { name: 'twitter:image', content: `${SITE_URL}/og-image.png` },
];

export const links: LinksFunction = () => [
  { rel: 'icon', type: 'image/png', href: '/favicon.png' },
  { rel: 'apple-touch-icon', href: '/logo.png' },
  { rel: 'manifest', href: '/manifest.json' },
  { rel: 'preconnect', href: 'https://accounts.google.com' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap',
  },
  { rel: 'stylesheet', href: globalStyles },
];

const STALE_TIME_MS = 60 * 1000;

function extractErrorMessage(error: unknown): string {
  return getErrorMessage(error, 'Something went wrong. Please try again.');
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: false,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Skip toast if the mutation has its own onError handler
        if (mutation.options.onError) return;
        toast.error(extractErrorMessage(error));
      },
    }),
  });
}

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  return (
    <html lang="en" dir="ltr">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:url" content={`${SITE_URL}${pathname}`} />
        <link rel="canonical" href={`${SITE_URL}${pathname}`} />
        <Meta />
        <Links />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              {
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: 'Deleg8 Studio',
                url: SITE_URL,
                logo: `${SITE_URL}/logo.png`,
                description: 'AI-powered creative marketing platform — delegate your creative work to AI.',
                foundingDate: '2026',
              },
              {
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: 'Deleg8 Studio',
                url: SITE_URL,
              },
              {
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: 'Deleg8 Studio',
                url: SITE_URL,
                image: `${SITE_URL}/logo.png`,
                applicationCategory: 'BusinessApplication',
                description: 'AI-powered creative marketing platform that captures brand context and generates multi-channel campaigns.',
                operatingSystem: 'Web',
                offers: { '@type': 'Offer', price: '19', priceCurrency: 'USD' },
                author: { '@type': 'Organization', name: 'Deleg8 Studio', url: SITE_URL },
              },
            ]),
          }}
        />
        {/* GA4 + Google OAuth scripts rendered unconditionally to avoid hydration mismatch */}
        <script
          async
          src={import.meta.env.VITE_GA_MEASUREMENT_ID ? `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA_MEASUREMENT_ID}` : undefined}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: import.meta.env.VITE_GA_MEASUREMENT_ID
              ? `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${import.meta.env.VITE_GA_MEASUREMENT_ID}',{send_page_view:false});`
              : '',
          }}
        />
        <script
          src={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ? 'https://accounts.google.com/gsi/client' : undefined}
          async
          defer
        />
      </head>
      <body>
        {/* Config scripts rendered unconditionally to avoid hydration mismatch */}
        <script
          dangerouslySetInnerHTML={{
            __html: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID
              ? `window.__GOOGLE_OAUTH_CLIENT_ID__=${JSON.stringify(import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID)};`
              : '',
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: import.meta.env.VITE_SENTRY_DSN
              ? `window.__SENTRY_DSN__=${JSON.stringify(import.meta.env.VITE_SENTRY_DSN)};`
              : '',
          }}
        />
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-xl focus:bg-creative-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
        >
          Skip to main content
        </a>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App(): ReactNode {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <RadixTooltip.Provider delayDuration={300}>
        <Outlet />
        <Toaster position="bottom-right" richColors closeButton />
      </RadixTooltip.Provider>
    </QueryClientProvider>
  );
}

export function ErrorBoundary(): ReactNode {
  const error = useRouteError();

  if (sentryEnabled && !isRouteErrorResponse(error)) {
    Sentry.captureException(error);
  }

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again later.';

  if (isRouteErrorResponse(error)) {
    title = error.status === 404 ? 'Page not found' : `Error ${error.status}`;
    message = error.statusText || message;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-surface-900 via-creative-950 to-surface-900 px-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-creative-500 via-accent-500 to-heat-500 shadow-glow mb-6">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
      <h1 className="text-3xl font-bold text-white">{title}</h1>
      <p className="mt-2 text-sm text-white/60">{message}</p>
      <a
        href="/app"
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20"
      >
        Go to Home
      </a>
    </div>
  );
}
